import httpClient from 'shared/lib/httpClient';
import {
  clearLocalStorageData,
  checkRefreshTokenAuth,
} from 'shared/lib/authUtils';

/**
 * SA-1700 — Notify the backend to log the logout activity, then tear down the
 * client session.
 *
 * The API call is best-effort: a failure or timeout must never trap the user
 * in an authenticated state, so client teardown (storage clear + redirect)
 * always runs in `finally`. The request interceptor attaches the Bearer token
 * automatically, so the backend receives the token it needs to identify the
 * session being closed.
 *
 * Plain async util (not a hook) because it is called from a React handler
 * (Sidebar), a `useCallback` (activity tracker), and a non-hook `setInterval`
 * context (session-warning auto-timeout) — a hook/`useMutation` cannot cover
 * all three call sites.
 *
 * @param {{ redirectTo?: string }} [options]
 */
let isLoggingOut = false;

export const logoutSession = async ({ redirectTo = '/login' } = {}) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  try {
    // BE requires the refresh_token as `token` in the body to revoke the
    // session (SA-1700; the OpenAPI doc is outdated and omits this payload).
    // The Bearer access token is added separately by the request interceptor.
    await httpClient.post('/logout', { token: checkRefreshTokenAuth() });
  } catch (error) {
    console.error('[logoutSession] logout API failed:', {
      status: error?.response?.status,
      endpoint: '/logout',
    });
  } finally {
    clearLocalStorageData();
    window.location.href = redirectTo;
  }
};
